import React, { Fragment, useEffect } from 'react';
import {
  MenuItem,
  Tooltip,
  IconButton,
  Select,
  FormControl,
  InputLabel,
  FormGroup,
  FormControlLabel,
  Switch,
  TextField,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { Create, RestartAlt, Close } from '@mui/icons-material';
import FoodBank from '@mui/icons-material/FoodBank';
import { userRequest } from '../../../auth/requestMethods';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Link } from 'react-router-dom';
import Breadcrumb from '../../common/breadcrumb';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import LastUpdated from '../../common/LasUpdated';
import InputAdornment from '@mui/material/InputAdornment';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import { Helmet } from 'react-helmet-async';

const Manage = ({ history }) => {
  useEffect(() => {
    document.title = 'PFSA | SKUS | Edit';
  }, []);
  const [modalState, setModalState] = React.useState({ open: false, update: 0, values: null });
  const [filterOptions, setFilterOptions] = React.useState({
    skuTypes: [],
    uoms: [],
  });
  const [packets, setPackets] = React.useState([]);
  const [skuId, setSkuId] = React.useState(false);
  const [accountingDbs, setAccountingDbs] = React.useState([]);
  const [accountingCodeHelp, setAccountingCodeHelp] = React.useState({});
  const [currentlyValidating, setCurrentlyValidating] = React.useState([]);
  // Add database validating
  const addValidating = (item) => {
    setCurrentlyValidating((prevItems) => [...prevItems, item]);
  };

  // Remove database validating
  const removeValidating = (rem) => {
    setCurrentlyValidating((prevItems) => prevItems.filter((db) => db !== rem));
  };

  const validationSchema = Yup.object().shape({
    sku_type_id: Yup.string().required('Required'),
    quantity_in_unit_of_measure: Yup.string()
      .required('Required')
      .transform((value) => (parseInt(value) <= 0 ? NaN : value))
      .typeError('Incorrect input'),
    unit_of_measure_id: Yup.string().required('Required'),
    is_active: Yup.boolean().required(),
    accountingCode: getAccountingCodeValidationSchema(accountingDbs),
  });

  const {
    register,
    handleSubmit,
    watch,
    reset,
    trigger,
    control,
    getValues,
    formState,
    formState: { errors },
    setError,
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const { fields } = useFieldArray({
    control,
    name: 'accountingCode',
  });

  const values = getValues();

  async function validateAccountingCode(db) {
    const code = getValues().accountingCode[db].code;
    const skuId = getValues().id;

    if (code) {
      try {
        addValidating(db);

        const data = (await bulkAxios([`/accounting-databases/sku-accounting-code?SageDB=${db}&code=${code}`]))[0];

        removeValidating(db);

        if (
          data.skuCode.accountingCode &&
          (data.skuCode?.internalSku?.id === Number(skuId) || data.skuCode?.internalSku?.id === undefined)
        ) {
          setAccountingCodeHelp((currentState) => {
            return {
              ...currentState,
              [db]: data.skuCode.accountingDescription,
            };
          });
          return true;
        } else {
          const message = !data.skuCode.accountingCode
            ? `This code doesn't exist`
            : data.skuCode.internalSku
            ? `This code is linked to ${data.skuCode.internalSku.display_name}`
            : '';

          setError(`accountingCode.${db}.code`, { type: 'custom', message: message });
          return false;
        }
      } catch (error) {
        setError(`accountingCode.${db}.code`, { type: 'custom', message: 'Something went wrong when validating' });
        return false;
      }
    } else {
      setAccountingCodeHelp((currentState) => {
        return {
          ...currentState,
          [db]: undefined,
        };
      });
      return true;
    }
  }

  function getAccountingCodeValidationSchema(dbs) {
    const schemaFields = {};

    // Loop through each key in accountingCode and create a validation rule
    for (const key of dbs) {
      schemaFields[key] = Yup.object().shape({
        code: Yup.string().nullable(),
      });
    }

    return Yup.object().shape(schemaFields);
  }

  React.useEffect(() => {
    trigger();
    const subscription = watch((value, { name }) => trigger(name));
    return () => subscription.unsubscribe();
  }, [watch, trigger]);

  React.useEffect(() => {
    let id = window.location.pathname.split('/')[3];
    let urls = ['/sku-types/names', '/unit-of-measures', '/accounting-databases/'];
    if (id) {
      setSkuId(id);
      urls.push('/skus/retrieve/' + id, `/skus/pack-sizes?sku_id=${id}`);
      urls.push();
    }
    bulkAxios(urls)
      .then((all) => {
        setFilterOptions({
          skuTypes: all[0].skuTypes,
          uoms: all[1].unitOfMeasures,
        });
        setAccountingDbs(all[2].accountingDatabases ?? []);
        if (all.length > 3) {
          reset(all[3].sku);
          setPackets(all[4].skuPackSizes);

          trigger().then(Object.keys(getValues().accountingCode).map((db) => validateAccountingCode(db)));
        }
      })
      .catch((error) => console.log(error));
  }, [reset]);

  function closeModal() {
    setModalState({ update: 0, open: false, values: null });
  }

  function openModal(update = 0, vals = null) {
    setModalState({ update, open: true, values: vals });
  }

  async function onSave({ display_name, ...form }) {
    toast.info('Validating...', {
      isLoading: true,
      position: 'bottom-center',
      autoClose: 5000,
      theme: 'colored',
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      toastId: 'validating',
    });

    const valid = await Promise.all(Object.keys(form.accountingCode).map((db) => validateAccountingCode(db)));

    toast.dismiss('validating');

    if (valid.includes(false)) {
      return;
    }

    toast.info(`${skuId ? 'Updating...' : 'Creating...'}`, {
      isLoading: true,
      position: 'bottom-center',
      autoClose: 5000,
      theme: 'colored',
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      toastId: 'create',
    });

    let api = skuId ? `/skus/update/${skuId}` : '/skus';

    userRequest()
      .post(api, form)
      .then(({ data }) => {
        toast.dismiss('create');
        toast.success(`Successfully ${skuId ? 'updated' : 'created'}`, {
          position: 'bottom-center',
          autoClose: 5000,
          closeOnClick: true,
          draggable: true,
        });
        !skuId && setSkuId(data.sku.id);
        reset({ display_name, ...data.sku }, {});
      })
      .catch(({ response }) => {
        toast.dismiss('create');
        toast.error(response.data.message, {
          position: 'bottom-center',
          autoClose: 5000,
          closeOnClick: true,
          draggable: true,
        });
      });
  }

  const onSavePackSize = async (form) => {
    toast.info('saving...', {
      isLoading: true,
      position: 'bottom-center',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      toastId: 'save',
    });
    let api = modalState.update ? `/skus/pack-sizes/update/${modalState.update}` : '/skus/pack-sizes';
    try {
      const { data } = await userRequest().post(api, { ...form, sku_id: skuId });

      setModalState({ ...modalState, open: false });

      toast.dismiss('save');
      toast.success('Successfully saved', {
        position: 'bottom-center',
        autoClose: true,
        closeOnClick: true,
        draggable: true,
      });
      // update the state
      data.skuPackSize.id === modalState.update
        ? setPackets((rows) => rows.map((r) => (r.id === data.skuPackSize.id ? data.skuPackSize : r)))
        : setPackets((rows) => [...rows.map((r) => r), data.skuPackSize]);
    } catch ({ response }) {
      toast.dismiss('save');
      toast.error(response.data?.message || 'Something Went wrong', {
        position: 'bottom-center',
        autoClose: 5000,
        closeOnClick: true,
        draggable: true,
      });
      return null;
    }
  };

  function setPackSizeStatus(id, is_active) {
    userRequest()
      .post(`/skus/pack-sizes/update/${id}`, { is_active })
      .then(() => {
        setPackets((rows) => rows.map((r) => (r.id === id ? { ...r, is_active } : r)));
      })
      .catch(() => {});
  }

  const packSizecolumns = [
    {
      field: 'actions',
      headerName: '',
      headerAlign: 'center',
      width: 130,
      renderCell: ({ row }) => {
        return (
          <div className="d-flex justify-content-between align-items-center" style={{ cursor: 'pointer' }}>
            <IconButton onClick={() => setModalState({ open: true, update: row.id, values: row })}>
              <Tooltip title={'Edit'} placement="top">
                <Create style={{ color: '#28a745' }} />
              </Tooltip>
            </IconButton>
            <IconButton onClick={() => setPackSizeStatus(row.id, !row.is_active)}>
              <Tooltip title={row.is_active ? 'Deactivate' : 'Activate'} placement="top">
                {row.is_active ? <Close style={{ color: '#0e4bd9' }} /> : <RestartAlt style={{ color: '#c6be27' }} />}
              </Tooltip>
            </IconButton>
          </div>
        );
      },
    },
    {
      field: 'pack_size',
      headerName: 'Pack Sizes',
      type: 'number',
      flex: 1,
      editable: true,
    },
    {
      field: 'accounting_code',
      headerName: 'Accounting Code',
      editable: true,
      flex: 1,
    },
    {
      field: 'is_active',
      headerName: 'Status',
      type: 'singleSelect',
      valueOptions: [true, false],
      valueFormatter: ({ value }) => (value ? 'Active' : 'Inactive'),
      cellClassName: ({ value }) => {
        if (value) return 'text-success';
        else return 'text-danger';
      },
      editable: true,
      flex: 1,
    },
  ];

  return (
    <Fragment>
      <Breadcrumb
        link="/skus/manage"
        icon={<FoodBank />}
        title={skuId ? 'Edit' : 'Create'}
        parent="SKUs"
      />
      <Helmet>
        <title>PFSA | SKUs | {skuId ? 'Edit' : 'Create'}</title>
      </Helmet>
      <div className="col-sm-12">
        <div className="card">
          <div className="card-header form-group row mx-0">
            <h5>{skuId && `SKU ID : ${skuId}`}</h5>
          </div>
          {/* PACK SIZE MODAL */}
          <Modal toggle={closeModal} isOpen={modalState.open} size="lg" centered={true}>
            <ModalHeader toggle={closeModal}>{modalState.update ? 'Edit' : 'Create'} Pack Size</ModalHeader>
            <ModalBody>
              <div className="card-body">
                <PackSizeForm
                  handleClose={closeModal}
                  onSubmit={onSavePackSize}
                  values={modalState.values}
                  skuDisplayName={values.display_name}
                />
              </div>
            </ModalBody>
          </Modal>

          <div className="col-sm-12">
            <div className="card-body">
              <form className="theme-form" onSubmit={handleSubmit(onSave)}>
                <div className="form-group row mx-0">
                  <Controller
                    control={control}
                    name="sku_type_id"
                    defaultValue={''}
                    render={({ field }) => (
                      <FormControl error={!!errors.sku_type_id?.message && field.touched}>
                        <InputLabel id="demo-simple-select-helper-label">SKU Type</InputLabel>
                        <Select
                          name={field.name}
                          value={field.value}
                          onBlur={field.onBlur}
                          onChange={(e) => {
                            field.onChange(e.target.value);
                          }}
                          labelId="demo-simple-select-helper-label"
                          id="demo-simple-select-helper"
                          label="SKU Type"
                        >
                          <MenuItem value={''}>- Select -</MenuItem>
                          {filterOptions.skuTypes.map((sk, i) => (
                            <MenuItem key={i} value={sk.id}>
                              {sk.display_name}
                            </MenuItem>
                          ))}
                        </Select>
                        {/* <FormHelperText>{errors.sku_type_id?.message}</FormHelperText> */}
                      </FormControl>
                    )}
                  />
                </div>

                <div className="form-group d-flex gap-4 flex-wrap row mx-0">
                  <TextField
                    value={values.quantity_in_unit_of_measure}
                    sx={{ flex: 2 }}
                    type="number"
                    label="Quantity (in Unit of Measure)"
                    variant="outlined"
                    {...register(`quantity_in_unit_of_measure`)}
                    // helperText={errors.quantity_in_unit_of_measure?.message}
                    error={
                      !!errors.quantity_in_unit_of_measure?.message && getValues().quantity_in_unit_of_measure !== ''
                    }
                    inputProps={{
                      style: {
                        color: 'black',
                      },
                    }}
                  />
                  <Controller
                    control={control}
                    name="unit_of_measure_id"
                    defaultValue={''}
                    render={({ field }) => (
                      <FormControl sx={{ flex: 6 }} error={!!errors.unit_of_measure_id?.message && field.touched}>
                        <InputLabel id="demo-simple-select-helper-label">UOM</InputLabel>
                        <Select
                          name={field.name}
                          value={field.value}
                          onBlur={field.onBlur}
                          onChange={(e) => {
                            field.onChange(e.target.value);
                          }}
                          labelId="demo-simple-select-helper-label"
                          id="demo-simple-select-helper"
                          label="UOM"
                        >
                          <MenuItem value={''}>- Select -</MenuItem>
                          {filterOptions.uoms.map((uom, i) => (
                            <MenuItem key={i} value={uom.id}>
                              {uom.display_unit}
                            </MenuItem>
                          ))}
                        </Select>
                        {/* <FormHelperText>{errors.unit_of_measure_id?.message}</FormHelperText> */}
                      </FormControl>
                    )}
                  />
                </div>

                <Grid gap={3} width={'50%'}>
                  <Typography sx={{ paddingBottom: 3 }} variant="h6">
                    Accounting Codes
                  </Typography>
                  {accountingDbs.map((db, index) => {
                    return (
                      <div key={index} className="form-group d-flex gap-4 flex-wrap row mx-0">
                        <Stack
                          direction="row"
                          spacing={4}
                          sx={{
                            padding: 0,
                            flex: 1,
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <Typography variant="h6">{db}</Typography>
                          </div>

                          <Controller
                            render={() => (
                              <TextField
                                sx={{ flex: 11 }}
                                autoComplete="off"
                                label="Accounting Code"
                                {...register(`accountingCode.${db}.code`)}
                                error={!!errors.accountingCode?.[db]?.code}
                                onBlur={() => validateAccountingCode(db)}
                                helperText={errors.accountingCode?.[db]?.code?.message ?? accountingCodeHelp[db]}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      {currentlyValidating.includes(db) && <CloudSyncIcon />}
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            )}
                            name={`accountingCode.${db}.code`}
                            control={control}
                          />
                        </Stack>
                      </div>
                    );
                  })}
                </Grid>

                {skuId && (
                  <div className="d-flex justify-content-start my-4">
                    <FormGroup>
                      <FormControlLabel
                        control={<Switch {...register(`is_active`)} checked={values.is_active} />}
                        label="Active Status"
                        sx={{ margin: 0 }}
                        labelPlacement="start"
                      />
                    </FormGroup>
                  </div>
                )}
                {skuId && <LastUpdated user={values.last_updated_by_user} datetime={values.last_updated_datetime} />}
                <div>
                  <button
                    className="btn btn-primary me-1"
                    disabled={!formState.isValid || !formState.isDirty || currentlyValidating.length}
                    type="submit"
                  >
                    {skuId ? 'Save' : 'Create'}
                  </button>
                  <Link to={`${process.env.PUBLIC_URL}/skus/manage`}>
                    <button className="btn btn-secondary" type="button">
                      Cancel
                    </button>
                  </Link>
                </div>
                {/*!!skuId && (
                  <>
                    <br /> <br />
                    <button className="btn btn-primary me-1 mb-2" type="button" onClick={() => openModal()}>
                      Add Pack Size
                    </button>
                    <div className="form-group row mx-0">
                      <DataGrid hideFooter rows={packets} columns={packSizecolumns} autoHeight />
                    </div>
                  </>
                )*/}
              </form>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

function PackSizeForm({ skuDisplayName, onSubmit, handleClose, values }) {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    trigger,
    getValues,
    formState: { errors, isDirty, isValid },
  } = useForm({
    resolver: yupResolver(packSizeValidationSchema),
    defaultValues: defaultPackSizeValues,
  });

  React.useEffect(() => {
    if (values) {
      reset(
        Object.keys(defaultPackSizeValues).reduce((pr, ky) => {
          pr[ky] = values[ky];
          return pr;
        }, {})
      );
    }
    trigger();
    const subscription = watch((value, { name }) => trigger(name));
    return () => subscription.unsubscribe();
  }, [watch, trigger]);

  return (
    <form className="theme-form" onSubmit={handleSubmit(onSubmit)}>
      <Grid item xs={12} marginBottom={4}>
        <TextField fullWidth label={'SKU'} disabled value={skuDisplayName} />
      </Grid>
      <Grid item xs={12} marginBottom={3}>
        <TextField
          label="Pack Size"
          variant="outlined"
          {...register(`pack_size`)}
          type="number"
          error={!!errors.pack_size?.message}
          helperText={errors.pack_size?.message}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} marginBottom={3}>
        <TextField
          label="Accounting Code"
          variant="outlined"
          {...register(`accounting_code`)}
          error={!!errors.accounting_code?.message}
          helperText={errors.accounting_code?.message}
          fullWidth
        />
      </Grid>
      {values && (
        <Grid item xs={12}>
          <FormControlLabel
            control={<Switch {...register(`is_active`)} checked={getValues('is_active')} />}
            label="Active Status"
            sx={{ margin: 0 }}
            labelPlacement="start"
          />
        </Grid>
      )}
      {values && <LastUpdated user={values.last_updated_by_user} datetime={values.last_updated_datetime} />}
      <Grid item xs={12} marginBottom={3}>
        <div>
          <button className="btn btn-primary me-1" type="submit" disabled={!isDirty || !isValid}>
            {values ? 'Save' : 'Create'}
          </button>

          <button onClick={handleClose} className="btn btn-secondary" type="button">
            Cancel
          </button>
        </div>
      </Grid>
    </form>
  );
}

const defaultValues = {
  sku_type_id: '',
  quantity_in_unit_of_measure: '',
  unit_of_measure_id: '',
  is_active: true,
  accountingCode: {},
};
const defaultPackSizeValues = {
  accounting_code: '',
  is_active: true,
  pack_size: 0,
};

const packSizeValidationSchema = Yup.object().shape({
  accounting_code: Yup.string().required('Required'),
  pack_size: Yup.number()
    .required('Required')
    .transform((value) => (parseInt(value) <= 0 ? NaN : value))
    .typeError('Incorrect input'),
  is_active: Yup.boolean(),
});

function bulkAxios(urls = null) {
  if (!urls) return urls;
  let promises = urls.map((req) =>
    userRequest()
      .get(req)
      .then(({ data }) => {
        return data;
      })
      .catch((error) => {
        return null;
      })
  );

  return Promise.all(promises);
}

export default Manage;
